import { FunctionComponent } from 'react'
import { useParams } from 'react-router-dom'
import {
  rucksack,
  Link,
  TableCell,
  TableRow,
} from '@black-bear-energy/rucksack'
import {
  isEntityUser,
  useImpersonatingUserOrUser,
} from '../../state/modules/auth'
import { SiteRFPDocumentSummary } from '@black-bear-energy/black-bear-energy-common'
import { ReviewsIcon } from './ReviewsIcon'
import { RequiredDocumentsProgressBar } from './RequiredDocumentsProgressBar'
import { DateAndTime } from './DateAndTime'
import { TableCellWithDefault } from '../common/TableCellWithDefault'
import { SiteRFPPhaseChips } from '../common/SiteRFPPhaseChips'
import { TextWithTooltip } from '../common/TextWithTooltip'
import { SITE_RFP_TABLE_FIRST_CELL_PADDING_LEFT } from '../helpers/DocumentExchange/tableConstants'
import { formatTableDate } from '../helpers/formatTableDate'
import { formatSignificantFigures } from '../helpers/formatSignificantFigures'
import { getSearchFormattedText } from '../helpers/getSearchFormattedText'

export const SiteRFPSection: FunctionComponent<SiteRFPSectionProps> = ({
  siteRFPDocumentSummary,
  nested,
  customerRFPDashboardPath,
  searchString,
}) => {
  const { customerRFPId } = useParams<{ customerRFPId: string }>()
  const systemSize = siteRFPDocumentSummary.systemSize
    ? formatSignificantFigures(siteRFPDocumentSummary.systemSize) + ' MW'
    : ''
  const basePath =
    customerRFPDashboardPath ?? `/document-exchange/projects/${customerRFPId}`
  const siteRFPDashboardPath = `${basePath}/sites/${siteRFPDocumentSummary.siteRFPId}`

  const user = useImpersonatingUserOrUser()
  const showNTP = user && isEntityUser(user) == false
  const phase = siteRFPDocumentSummary.phase

  return (
    <TableRow
      tabIndex={0}
      sx={{
        cursor: 'pointer',
        transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': { backgroundColor: rucksack.palette.action.hover },
        '&:focus': {
          backgroundColor: rucksack.palette.action.selected,
        },
        position: 'relative',
      }}
    >
      <TableCell
        sx={{
          paddingLeft: nested
            ? SITE_RFP_TABLE_FIRST_CELL_PADDING_LEFT
            : undefined,
        }}
      >
        <Link
          href={siteRFPDashboardPath}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          sx={{
            // Override the pink hover and focus color defined in index.css.
            '&:hover, &:focus': {
              color: rucksack.palette.primary.main,
            },
          }}
        >
          <TextWithTooltip
            text={siteRFPDocumentSummary.name}
            searchString={searchString}
          />
        </Link>
      </TableCell>
      <TableCell>
        <RequiredDocumentsProgressBar
          required={siteRFPDocumentSummary.required}
        />
      </TableCell>
      <TableCell sx={{ paddingLeft: '30px' }}>
        <ReviewsIcon
          verified={siteRFPDocumentSummary.verified}
          pendingReview={siteRFPDocumentSummary.pendingReview}
        />
      </TableCell>
      <TableCellWithDefault>{systemSize}</TableCellWithDefault>
      <TableCellWithDefault>
        <SiteRFPPhaseChips phase={phase} scale="small" direction="column" />
      </TableCellWithDefault>
      {showNTP && (
        <TableCellWithDefault>
          {siteRFPDocumentSummary.ntpEstimate &&
            formatTableDate(siteRFPDocumentSummary.ntpEstimate)}
        </TableCellWithDefault>
      )}
      <TableCell
        sx={{
          paddingY: 0,
        }}
      >
        {getSearchFormattedText(siteRFPDocumentSummary.address, searchString)}
        <br />
        {getSearchFormattedText(
          siteRFPDocumentSummary.cityStateZip,
          searchString
        )}
        {siteRFPDocumentSummary.section && (
          <>
            <br />
            {getSearchFormattedText(
              siteRFPDocumentSummary.section,
              searchString
            )}
          </>
        )}
      </TableCell>
      <TableCell>
        <TextWithTooltip
          text={siteRFPDocumentSummary.channelPartnerName}
          // channel partner is not searchable on Customer RFP pages
          searchString={nested ? searchString : undefined}
        />
      </TableCell>
      <TableCell>
        <DateAndTime date={siteRFPDocumentSummary.lastUpdated} />
      </TableCell>
      <TableCellWithDefault>
        {siteRFPDocumentSummary.updatedBy && (
          <TextWithTooltip
            text={siteRFPDocumentSummary.updatedBy}
            variant="body2"
          />
        )}
      </TableCellWithDefault>
    </TableRow>
  )
}

SiteRFPSection.displayName = 'SiteRFPSection'

interface SiteRFPSectionProps {
  siteRFPDocumentSummary: SiteRFPDocumentSummary
  nested?: boolean
  customerRFPDashboardPath?: string
  searchString?: string
}
